import { Injectable, signal } from '@angular/core';
import { TranslateService as NgxTranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class AppTranslateService {
  private readonly STORAGE_KEY = 'jonke_lang';
  readonly supportedLangs = ['en', 'de'];
  readonly defaultLang = 'de';
  readonly currentLangSignal = signal(this.defaultLang);

  constructor(private translate: NgxTranslateService) {}

  init(): void {
    this.translate.addLangs(this.supportedLangs);
    this.translate.setDefaultLang(this.defaultLang);

    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored && this.supportedLangs.includes(stored)) {
      this.translate.use(stored);
      this.currentLangSignal.set(stored);
      return;
    }

    // Auto-detect from browser
    const browserLang = this.translate.getBrowserLang() ?? '';
    const lang = this.supportedLangs.includes(browserLang) ? browserLang : this.defaultLang;
    this.translate.use(lang);
    this.currentLangSignal.set(lang);
    localStorage.setItem(this.STORAGE_KEY, lang);
  }

  get currentLang(): string {
    return this.currentLangSignal();
  }

  get currentLocale(): string {
    return this.toLocale(this.currentLang);
  }

  toLocale(lang: string): string {
    return ({ de: 'de-DE', en: 'en-GB' } as const)[lang as 'de' | 'en'] ?? 'de-DE';
  }

  switchLang(lang: string): void {
    if (this.supportedLangs.includes(lang)) {
      this.translate.use(lang);
      this.currentLangSignal.set(lang);
      localStorage.setItem(this.STORAGE_KEY, lang);
    }
  }

  toggle(): void {
    const next = this.currentLang === 'en' ? 'de' : 'en';
    this.switchLang(next);
  }
}
